/**
 * Fabrication Part Tool
 * 
 * Places LOD 400 fabrication parts from a service.
 */

import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { FabricationManager } from '../core/FabricationManager';

export class FabricationPartTool extends BaseTool {
    id = 'fabrication_part';
    name = 'Fabrication Part';
    cursor = 'crosshair';

    private manager: FabricationManager;
    private currentService: string = 'Supply Air 2in WG';
    private currentCid: number = 1; // Default to Rect Duct

    private currentPoint: THREE.Vector3 | null = null;
    private rotation: number = 0;

    constructor() {
        super();
        this.manager = new FabricationManager();
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (this.currentPoint) {
            this.createPart();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === ' ') {
            this.rotation += Math.PI / 2;
            if (this.rotation >= Math.PI * 2) this.rotation = 0;
            this.updatePreview();
            event.preventDefault();
        } else if (event.key === 'Escape') {
            this.deactivate();
        } else if (event.key === 'Tab') {
            // Cycle parts for demo
            this.cyclePart();
            event.preventDefault();
        }
    }

    private cyclePart() {
        // Toggle between Duct (1) and Elbow (2)
        this.currentCid = this.currentCid === 1 ? 2 : 1;
        this.updatePreview();
    }

    private updatePreview() {
        if (!this.context) return;

        if (!this.currentPoint) {
            this.context.appStore.setToolPreview(null);
            return;
        }

        const color = this.currentCid === 1 ? '#00aaaa' : '#aaaa00'; // Cyan for Duct, Yellow for Elbow

        this.context.appStore.setToolPreview({
            type: 'box', // Placeholder visualization
            position: this.currentPoint,
            size: [0.5, 0.5, 0.5], // Dummy size
            rotation: [0, 0, this.rotation],
            color: color
        });
    }

    private createPart() {
        if (!this.context || !this.currentPoint) return;

        const part = this.manager.createPart(
            this.currentCid,
            this.currentService,
            [this.currentPoint.x, this.currentPoint.y, this.currentPoint.z]
        );

        if (part) {
            // Apply current rotation (Manager returns 0,0,0)
            part.geometryData.rotation = [0, 0, this.rotation];
            this.context.bimStore.addElement(part);
            console.log(`Placed Fabrication Part: ${part.type} (CID ${part.partCid})`);
        }
    }
}
